/**
 * Inner panel body for the dashboard's Superpower card. Designed to live
 * inside the right-column <Collapsible> wrapper alongside Personalized
 * Insights / Resume Makeover / Interview Test Drive — so the visual size +
 * collapsibility match its siblings.
 *
 * Self-contained: fetches /api/network/pitch/latest, lets the user
 *   - "Resmo, what's mine?"  (free) — suggest + save a draft pitch
 *   - "Paint my avatar"      (5 cr) — Gemini Nano Banana image gen
 *   - "Repaint"              (5 cr)
 *   - "Refine in Network"    → /network?t=pitch
 *
 * The avatar generator supports gender-neutral / feminine / masculine
 * presentations; users pick one in the controls above the paint button so
 * the result matches who they actually are.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Loader, Wand2, ImageIcon, ArrowRight, RotateCw, Upload, X,
} from "lucide-react";
import { api, friendlyAxiosError } from "../../lib/api";

const PRESENTATIONS = [
  { id: "neutral",  label: "Neutral",  hint: "Androgynous silhouette" },
  { id: "feminine", label: "Feminine", hint: "Feminine-presenting silhouette" },
  { id: "masculine", label: "Masculine", hint: "Masculine-presenting silhouette" },
];

export default function DashboardSuperpowerPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pitch, setPitch] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [painting, setPainting] = useState(false);
  const [error, setError] = useState("");
  const [presentation, setPresentation] = useState("neutral");
  const fileRef = useRef(null);
  const [photoName, setPhotoName] = useState("");

  const reload = async () => {
    try {
      const { data } = await api.get("/network/pitch/latest");
      setPitch(data && data.id ? data : null);
    } catch { /* anon → ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const suggest = async () => {
    setError("");
    setSuggesting(true);
    try {
      const { data } = await api.post("/network/pitch/suggest-superpower");
      // Persist as a draft pitch so the avatar endpoint has something to anchor.
      await api.post("/network/pitch", {
        target_role: "General positioning",
        audience: null,
        superpower: data.superpower,
      });
      await reload();
    } catch (e) {
      setError(friendlyAxiosError(e));
    } finally {
      setSuggesting(false);
    }
  };

  const paint = async () => {
    setError("");
    setPainting(true);
    try {
      const fd = new FormData();
      fd.append("presentation", presentation);
      const photo = fileRef.current?.files?.[0];
      if (photo) fd.append("photo", photo);
      const { data } = await api.post("/network/pitch/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPitch((p) => p ? { ...p, superpower_avatar_url: data.avatar_url } : p);
    } catch (e) {
      setError(friendlyAxiosError(e));
    } finally {
      setPainting(false);
    }
  };

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    setPhotoName(f ? f.name : "");
  };

  const clearPhoto = () => {
    if (fileRef.current) fileRef.current.value = "";
    setPhotoName("");
  };

  const sp = pitch?.superpower || "";
  const avatarUrl = pitch?.superpower_avatar_url || "";

  return (
    <div data-testid="superpower-panel">
      {/* Top row: avatar slot + superpower text */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
        <div className="sm:col-span-4 flex justify-center sm:justify-start">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Your superpower"
              className="rounded-md"
              style={{
                width: "100%", maxWidth: 160, aspectRatio: "1 / 1", objectFit: "cover",
                border: "4px solid #0F1B2D", boxShadow: "4px 4px 0 0 #FFC145",
              }}
              data-testid="superpower-avatar-img"
            />
          ) : (
            <div
              className="flex items-center justify-center rounded-md"
              style={{
                width: "100%", maxWidth: 160, aspectRatio: "1 / 1",
                background: "white", border: "4px dashed #0F1B2D",
                color: "#0F1B2D",
              }}
              data-testid="superpower-avatar-placeholder"
            >
              <div className="text-center px-3">
                <ImageIcon size={26} className="mx-auto mb-1.5" style={{ color: "#FF6A00" }} />
                <div className="font-mono-rm text-[9px] uppercase tracking-widest" style={{ fontWeight: 700 }}>
                  Avatar awaits
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="sm:col-span-8">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1.5"
               style={{ color: "#92400E", fontWeight: 700 }}>
            · The thing recruiters remember
          </div>
          {loading ? (
            <div className="font-mono-rm text-sm" style={{ color: "#475569" }}>Loading…</div>
          ) : sp ? (
            <p
              className="font-display leading-snug"
              style={{ fontSize: "clamp(0.95rem, 1.9vw, 1.2rem)", color: "#0F1B2D", fontFamily: "Georgia, serif" }}
              data-testid="superpower-text"
            >
              "{sp}"
            </p>
          ) : (
            <p className="text-sm" style={{ color: "#334155" }}>
              Let Resmo read your resume + latest skill assessment and surface
              the one thing recruiters would remember about you 30 seconds
              after meeting.
            </p>
          )}
        </div>
      </div>

      {/* Controls row */}
      {sp && (
        <div className="mt-4 p-3 rounded-md" style={{ background: "rgba(255,193,69,0.18)", border: "2px solid rgba(255,193,69,0.6)" }}>
          {/* Presentation toggle */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#92400E", fontWeight: 700 }}>
              · Presentation
            </span>
            <div className="inline-flex rounded-md overflow-hidden" style={{ border: "2px solid #0F1B2D" }}
                 data-testid="superpower-presentation-switch">
              {PRESENTATIONS.map((p, idx) => {
                const active = presentation === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPresentation(p.id)}
                    className="font-mono-rm text-[10px] uppercase tracking-widest px-2 py-1"
                    style={{
                      background: active ? "#0F1B2D" : "white",
                      color: active ? "#FFC145" : "#0F1B2D",
                      borderLeft: idx === 0 ? "none" : "2px solid #0F1B2D",
                      fontWeight: 700,
                    }}
                    data-testid={`superpower-presentation-${p.id}`}
                    title={p.hint}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Photo upload row */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="rm-btn rm-btn-ghost text-[11px] px-2.5 py-1.5"
              data-testid="superpower-photo-pick"
              title="Optional: upload a selfie. Resmo will keep the editorial style but ground it on your face."
            >
              <Upload size={11} /> Use my photo (optional)
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                   onChange={onPickPhoto} className="hidden" data-testid="superpower-photo-input" />
            {photoName && (
              <span className="inline-flex items-center gap-1.5 font-mono-rm text-[10px] uppercase tracking-widest px-2 py-1"
                    style={{ background: "white", border: "2px solid #0F1B2D", borderRadius: 4 }}
                    data-testid="superpower-photo-name">
                {photoName.length > 24 ? `${photoName.slice(0, 24)}…` : photoName}
                <button onClick={clearPhoto} aria-label="Clear photo"
                        style={{ color: "#991B1B" }}><X size={10} /></button>
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {!avatarUrl ? (
              <button
                onClick={paint}
                disabled={painting}
                className="rm-btn rm-btn-primary text-xs px-3 py-2"
                style={{ boxShadow: "3px 3px 0 0 #0F1B2D", fontWeight: 700 }}
                data-testid="superpower-paint-btn"
              >
                {painting ? <Loader size={13} className="animate-spin" /> : <Wand2 size={13} />}
                {painting ? "Painting… (15-25s)" : "Paint my avatar (5 cr)"}
              </button>
            ) : (
              <button
                onClick={paint}
                disabled={painting}
                className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                data-testid="superpower-repaint-btn"
              >
                {painting ? <Loader size={13} className="animate-spin" /> : <RotateCw size={13} />}
                {painting ? "Repainting…" : "Repaint (5 cr)"}
              </button>
            )}
            <button
              onClick={() => navigate("/network?t=pitch")}
              className="rm-btn rm-btn-secondary text-xs px-3 py-2"
              data-testid="superpower-refine-btn"
            >
              <Sparkles size={13} /> Refine in Network <ArrowRight size={11} />
            </button>
          </div>
        </div>
      )}

      {/* Empty state CTAs */}
      {!loading && !sp && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={suggest}
            disabled={suggesting}
            className="rm-btn rm-btn-primary text-xs px-3 py-2"
            style={{ boxShadow: "3px 3px 0 0 #0F1B2D", fontWeight: 700 }}
            data-testid="superpower-suggest-btn"
          >
            {suggesting ? <Loader size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {suggesting ? "Resmo is reading you…" : "Resmo, what's mine? (free)"}
          </button>
          <button
            onClick={() => navigate("/network?t=pitch")}
            className="rm-btn rm-btn-ghost text-xs px-3 py-2"
            data-testid="superpower-network-link"
          >
            Or write it myself →
          </button>
        </div>
      )}

      {error && (
        <div className="mt-3 font-mono-rm text-xs"
             style={{ color: "#991B1B" }}
             data-testid="superpower-error">
          {error}
        </div>
      )}
    </div>
  );
}
